/**
 * BED ENGAGEMENT CARD
 * Zone administrative « Bulletin d'engagement » de la fiche équipage
 * (migration 00232, cahier L15-16 et L71-76).
 *
 * Ancrage : la fiche ÉQUIPAGE et pas la fiche contact. Le BED est émis par
 * `bed_access_tokens.crew_id`, le solde par `payment_cases.crew_id`, et le
 * statut CRM vit sur `crews.status` (le contact le reçoit par la synchro
 * 00024/00109). Tout ce que cette zone montre est donc crew-scopé ; la dupliquer
 * sur le contact afficherait la même donnée deux fois.
 *
 * Elle montre : le statut BED courant, les éléments manquants (avec leur date
 * d'engagement « promis avant le … »), les signatures, le certificat médical et
 * l'état du solde. Elle permet : lever un élément à la main, forcer le statut,
 * et émettre le lien du formulaire.
 *
 * Toute la vérité vient du serveur (`get_crew_bed_overview`) : ce composant ne
 * recalcule ni la liste des manquants, ni le statut cible. Chaque action
 * renvoie l'état d'après, qu'on affiche tel quel.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  Check,
  ClipboardList,
  Copy,
  ExternalLink,
  FileText,
  LinkIcon,
  Mail,
  Ban,
  Plus,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Select, toast } from '../ui';
import { BedFormLinkDialog } from './BedFormLinkDialog';
import { BedRevokeLinksDialog } from './BedRevokeLinksDialog';
import {
  bedAdminErrorMessage,
  buildBedFormLink,
  getCrewBedOverview,
  getCrewBedSoldePreview,
  getCrewDerogationBlock,
  addBedCustomMissingItem,
  getBedCertificateSignedUrl,
  removeBedCustomMissingItem,
  resolveBedMissingItem,
  reviewBedCertificate,
  sendBedMissingReminder,
  setCrewBedStatus,
  unresolveBedMissingItem,
} from '../../lib/bed/admin';
import type {
  BedAdminMissingItem,
  BedCrewOverview,
  BedDerogationBlock,
  BedSoldePreview,
} from '../../lib/bed/admin';
import { BedDerogationBlockBanner } from './BedDerogationBlockBanner';
import { usePermissions } from '../../hooks/usePermissions';
import { CREW_STATUS_COLORS, CREW_STATUS_LABELS } from '../../types/crew';
import type { CrewStatus } from '../../types/crew';

// Statuts que l'admin peut forcer depuis cette zone — miroir exact de la garde
// de `bed_set_crew_status` (00232). Les annulations et reports gardent leur
// propre chemin (bloc « Informations générales »), on ne les duplique pas ici.
// Statuts du parcours BED (mêmes que l'ouverture, 00293) : hors parcours
// (annulé, reporté…), conseiller « Forcer le statut BED » serait dangereux.
const BED_PARCOURS_STATUSES = [
  'pre_registered',
  'bed_downloaded',
  'bed_incomplete',
  'bed_incomplete_payment',
];

// 00305 : une dette réapparue ne se signale que dans le parcours BED (un
// équipage annulé, reporté ou archivé n'a rien à régler). 00306 : l'inscrit
// définitif a son propre signal (`debt_after_final_registration`).
const BED_DEBT_STATUSES = [...BED_PARCOURS_STATUSES, 'bed_complete'];

const OVERRIDABLE_STATUSES: CrewStatus[] = [
  'pre_registered',
  'bed_downloaded',
  'bed_incomplete',
  'bed_incomplete_payment',
  'bed_complete',
  'completed',
];

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  return (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

/** Une ligne « élément manquant » avec son action de levée. */
function MissingItemRow({
  item,
  busy,
  onResolve,
  onUnresolve,
  onRemove,
}: {
  item: BedAdminMissingItem;
  busy: boolean;
  onResolve: (item: BedAdminMissingItem) => void;
  onUnresolve: (item: BedAdminMissingItem) => void;
  /** 00253 : retrait d'un élément personnalisé (kind === 'custom'). */
  onRemove?: (item: BedAdminMissingItem) => void;
}) {
  return (
    <li
      className={`flex items-start justify-between gap-3 rounded-md border px-3 py-2 ${
        item.resolved ? 'border-emerald-200 bg-emerald-50' : 'border-orange-200 bg-orange-50'
      }`}
    >
      <div className="min-w-0">
        <p
          className={`text-sm font-medium ${item.resolved ? 'text-emerald-800 line-through' : 'text-orange-900'}`}
        >
          {item.label}
        </p>
        <p className="mt-0.5 text-xs text-zinc-600">
          {item.role_label}
          {item.kind === 'deferred' ? (
            <>
              {' · '}
              <span className="text-zinc-500">
                promis avant le {formatDate(item.due_date ?? null)}
              </span>
            </>
          ) : item.kind === 'custom' ? (
            <>
              {' · '}
              <span className="text-zinc-500">ajouté par l’organisation</span>
              {item.custom_note && <span className="text-zinc-500"> — {item.custom_note}</span>}
            </>
          ) : (
            <>
              {' · '}
              <span className="text-zinc-500">non renseigné</span>
            </>
          )}
          {item.resolved && (
            <>
              {' · '}
              <span className="text-emerald-700">validé le {formatDate(item.resolved_at)}</span>
            </>
          )}
        </p>
        {item.resolved && item.note && (
          <p className="mt-0.5 text-xs text-emerald-700 italic">« {item.note} »</p>
        )}
      </div>
      {item.kind === 'custom' && onRemove && (
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => onRemove(item)}
          className="shrink-0 text-zinc-500 hover:text-red-700"
          title="Retirer cet élément"
        >
          <Trash2 className="size-3.5" />
        </Button>
      )}

      {item.resolved ? (
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => onUnresolve(item)}
          className="shrink-0 text-zinc-500 hover:text-zinc-900"
        >
          <RotateCcw className="mr-1.5 size-3.5" />
          Annuler
        </Button>
      ) : (
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => onResolve(item)}
          className="shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
        >
          <Check className="mr-1.5 size-3.5" />
          Valider
        </Button>
      )}
    </li>
  );
}

interface BedEngagementCardProps {
  crewId: string;
  crewName: string;
  /** Remonte à la fiche que le statut de l'équipage a bougé (badge, timeline). */
  onStatusChanged?: () => void;
  /**
   * TICKET-DEROG-04 : empreinte des membres (rôle, date de naissance, case
   * « Dérogation validée »). Quand elle change, la raison du blocage du solde
   * est relue — la case se coche sur une autre carte de la fiche.
   */
  derogationKey?: string;
}

export function BedEngagementCard({
  crewId,
  crewName,
  onStatusChanged,
  derogationKey,
}: BedEngagementCardProps) {
  const { canEditCrewSensitiveFields } = usePermissions();
  const isStaff = canEditCrewSensitiveFields();

  const [overview, setOverview] = useState<BedCrewOverview | null>(null);
  const [solde, setSolde] = useState<BedSoldePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string>('');
  // TICKET-DEROG-04 : raison du blocage du solde, relue en base (00310). Le
  // résultat est rattaché à l'équipage et à l'empreinte des membres qui l'ont
  // demandé : un résultat d'un autre équipage, ou d'avant un changement de
  // membre, ne vaut pas. `block` null = contrôle en échec.
  const [derogationResult, setDerogationResult] = useState<{
    crewId: string;
    key: string | undefined;
    block: BedDerogationBlock | null;
  } | null>(null);
  const derogationCheck =
    derogationResult?.crewId === crewId && derogationResult.key === derogationKey
      ? derogationResult
      : null;
  const derogation = derogationCheck?.block ?? null;

  const load = useCallback(async () => {
    setLoading(true);
    const result = await getCrewBedOverview(crewId);
    if (result.status === 'ok') {
      setOverview(result.data);
      setForbidden(false);
      // Le total vivant compte les options du dossier courant (00295). Hors
      // parcours BED, le bandeau ne s'affiche pas : inutile de le calculer.
      setSolde(
        // 00296 : « rien à payer » sans dossier, le surplus se lit aussi une fois
        // le bulletin validé (le participant le voit sur son récapitulatif).
        BED_PARCOURS_STATUSES.includes(result.data.bed_status) || result.data.payment.nothing_due
          ? await getCrewBedSoldePreview(crewId, result.data.payment.payment_case_id)
          : null
      );
    } else {
      setOverview(null);
      setSolde(null);
      setForbidden(result.status === 'forbidden');
    }
    setLoading(false);
  }, [crewId]);

  useEffect(() => {
    if (!isStaff) {
      setLoading(false);
      return;
    }
    void load();
  }, [isStaff, load]);

  // TICKET-DEROG-04 : relue à l'ouverture et dès que les membres changent.
  useEffect(() => {
    if (!isStaff) return;
    let cancelled = false;
    void getCrewDerogationBlock(crewId).then((block) => {
      if (!cancelled) setDerogationResult({ crewId, key: derogationKey, block });
    });
    return () => {
      cancelled = true;
    };
  }, [isStaff, crewId, derogationKey]);

  // Chaque action renvoie l'état d'après : on l'applique sans recharger, et on
  // prévient la fiche si le statut a bougé (badge d'en-tête + timeline).
  const applyResult = useCallback(
    (
      result: Awaited<ReturnType<typeof resolveBedMissingItem>>,
      previousStatus: string | undefined
    ) => {
      if (result.status !== 'ok') {
        toast.error(bedAdminErrorMessage(result.status));
        return;
      }
      setOverview(result.data);
      if (result.data.bed_status !== previousStatus) {
        toast.success(`Statut BED : ${result.data.bed_status_label}`);
        onStatusChanged?.();
      }
    },
    [onStatusChanged]
  );

  async function handleResolve(item: BedAdminMissingItem) {
    setBusy(true);
    const previous = overview?.bed_status;
    const result = await resolveBedMissingItem(crewId, item.code);
    applyResult(result, previous);
    if (result.status === 'ok') toast.success(`« ${item.label} » validé manuellement`);
    setBusy(false);
  }

  async function handleUnresolve(item: BedAdminMissingItem) {
    setBusy(true);
    const previous = overview?.bed_status;
    const result = await unresolveBedMissingItem(crewId, item.code);
    applyResult(result, previous);
    setBusy(false);
  }

  // ---- 00253 : manquants personnalisés, relance, certificats (recette 4L) ----
  const [customLabel, setCustomLabel] = useState('');
  const [viewedCertificates, setViewedCertificates] = useState<
    Partial<Record<'pilot' | 'copilot', string>>
  >({});

  async function handleAddCustom() {
    const label = customLabel.trim();
    if (!label) return;
    setBusy(true);
    const previous = overview?.bed_status;
    const result = await addBedCustomMissingItem(crewId, label);
    applyResult(result, previous);
    if (result.status === 'ok') {
      toast.success(`« ${label} » ajouté aux éléments manquants`);
      setCustomLabel('');
    }
    setBusy(false);
  }

  async function handleRemoveCustom(item: BedAdminMissingItem) {
    if (!item.item_id) return;
    setBusy(true);
    const previous = overview?.bed_status;
    const result = await removeBedCustomMissingItem(crewId, item.item_id);
    applyResult(result, previous);
    setBusy(false);
  }

  async function handleSendReminder() {
    setBusy(true);
    const result = await sendBedMissingReminder(crewId);
    if (result.status === 'ok') {
      // Recette du 07/10 : « mise en file » laissait croire à un envoi immédiat.
      // Sans exécution automatique (ni cron ni URL dans le Vault), la file n'est
      // traitée qu'au clic « Exécuter maintenant ».
      toast.success(
        `Relance ajoutée à la file vers ${result.recipient} (${result.missingCount} élément(s) manquant(s)), pas encore envoyée`,
        {
          description:
            'Elle part au prochain passage des automatisations BED. Sans exécution automatique, un admin le déclenche dans Admin > Automatisations BED > « Exécuter maintenant » (ce clic traite aussi les autres emails en attente et les relances actives).',
          duration: 10000,
        }
      );
    } else if (result.status === 'no_active_link') {
      toast.error(
        'Générez un lien BED actif avant de relancer cet équipage. Le brouillon sera conservé.'
      );
    } else if (result.status === 'no_recipient') {
      toast.error('Aucune adresse e-mail pour cet équipage.');
    } else if (result.status === 'no_template') {
      toast.error('Le modèle d’e-mail « informations manquantes » n’est pas configuré.');
    } else {
      toast.error(bedAdminErrorMessage(result.status));
    }
    setBusy(false);
  }

  async function handleViewCertificate(role: 'pilot' | 'copilot', path: string) {
    const url = await getBedCertificateSignedUrl(path);
    if (!url) {
      toast.error('Impossible d’ouvrir le certificat.');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
    setViewedCertificates((previous) => ({ ...previous, [role]: path }));
  }

  async function handleReviewCertificate(
    role: 'pilot' | 'copilot',
    status: 'validated' | 'rejected'
  ) {
    const expectedPath = viewedCertificates[role];
    if (!expectedPath) return;
    const note =
      status === 'rejected'
        ? (window.prompt(
            'Motif du refus (transmis dans la relance) :',
            'Certificat non conforme'
          ) ?? null)
        : null;
    if (status === 'rejected' && note === null) return;
    setBusy(true);
    const previous = overview?.bed_status;
    const result = await reviewBedCertificate(
      crewId,
      role,
      status,
      expectedPath,
      note ?? undefined
    );
    applyResult(result, previous);
    if (result.status === 'certificate_changed') {
      setViewedCertificates((paths) => ({ ...paths, [role]: undefined }));
      await load();
    }
    if (result.status === 'ok') {
      toast.success(
        status === 'validated' ? 'Certificat validé' : 'Certificat refusé — élément manquant ajouté'
      );
    }
    setBusy(false);
  }

  // (00242) Le lien ACTIF se COPIE, il ne se régénère pas : l'émission est
  // devenue idempotente côté serveur (create_bed_access_token rend le jeton
  // existant), et régénérer n'a de toute façon plus aucun effet. Le jeton est
  // déjà dans l'overview — aucun appel serveur n'est nécessaire ici.
  async function handleCopyActiveLink() {
    if (!overview?.link || overview.link.expired) return;
    let url: string;
    try {
      url = buildBedFormLink(overview.link.token);
    } catch (err) {
      // VITE_SITE_4LT_URL absente : on ne fabrique JAMAIS un lien CRM de repli,
      // il serait un cul-de-sac pour le participant (cf. buildBedFormLink).
      console.error('[BED-ADMIN] lien du formulaire:', err);
      toast.error(
        "L'origine du site 4L Trophy n'est pas configurée : lien impossible à construire."
      );
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Lien copié dans le presse-papiers');
    } catch {
      toast.error('Impossible de copier le lien.');
    }
  }

  async function handleApplyStatus() {
    if (!pendingStatus) return;
    setBusy(true);
    const previous = overview?.bed_status;
    const result = await setCrewBedStatus(crewId, pendingStatus);
    applyResult(result, previous);
    if (result.status === 'ok') setPendingStatus('');
    setBusy(false);
  }

  // Zone strictement réservée au staff : même périmètre que la garde serveur
  // (`superadmin`/`admin`), pas d'écran vide trompeur pour les autres.
  if (!isStaff) return null;

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2">
          <ClipboardList className="size-5 text-zinc-400" />
          <h2 className="text-xl font-semibold text-zinc-900">Bulletin d'engagement</h2>
        </div>
        <p className="mt-2 text-sm text-zinc-400">Chargement...</p>
      </Card>
    );
  }

  if (forbidden || !overview) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2">
          <ClipboardList className="size-5 text-zinc-400" />
          <h2 className="text-xl font-semibold text-zinc-900">Bulletin d'engagement</h2>
        </div>
        <p className="mt-2 text-sm text-zinc-400">
          {forbidden
            ? "Vous n'avez pas accès à la zone BED."
            : 'Impossible de charger le bulletin d’engagement.'}
        </p>
      </Card>
    );
  }

  const status = overview.bed_status as CrewStatus;
  const colors = CREW_STATUS_COLORS[status] ?? {
    bg: 'bg-zinc-50',
    text: 'text-zinc-700',
    border: 'border-zinc-200',
  };
  const outstanding = overview.missing.items.filter((i) => !i.resolved);
  const resolved = overview.missing.items.filter((i) => i.resolved);
  const signatures = overview.missing.signatures ?? {};
  const certificates = overview.missing.certificates ?? {};
  // TICKET-DEROG-04 : affiché dès que la règle bloque, quelle que soit la
  // situation du solde (rien à payer : l'envoi du bulletin est bloqué ; réglé :
  // à vérifier avant l'inscription définitive). Dates manquantes : seulement
  // dans le parcours BED, solde non réglé.
  const derogationContext = overview.payment.nothing_due
    ? 'nothing_due'
    : overview.payment.settled
      ? 'settled'
      : 'payment';
  const showDerogationMissing =
    !overview.payment.settled && BED_DEBT_STATUSES.includes(overview.bed_status);
  // Recette du 07/10 : total vivant ≤ 0 sans encaissement, dans le parcours.
  // 00305 (TICKET-BED-32) : jamais quand l'acompte n'est pas porté par
  // l'équipage (il peut couvrir un autre équipage : ne pas inviter à forcer le
  // statut) ni quand une dette est réapparue.
  const nothingDue =
    !!solde &&
    solde.live_total_cents <= 0 &&
    !overview.payment.settled &&
    !(overview.payment.paid_cents ?? 0) &&
    !overview.payment.acompte_not_on_crew &&
    !overview.payment.acompte_missing &&
    !overview.payment.contact_link_active &&
    !overview.payment.debt_reopened &&
    BED_PARCOURS_STATUSES.includes(overview.bed_status);
  // (00242) Lien encore valide : on le COPIE au lieu de proposer une émission
  // qui, côté serveur, rendrait exactement le même jeton.
  const hasActiveLink = !!overview.link && !overview.link.expired;
  const debtReopened =
    !!overview.payment.debt_reopened && BED_DEBT_STATUSES.includes(overview.bed_status);
  // 00306 (TICKET-BED-32 Y2) : même dette sur un équipage déjà Inscrit définitif.
  const debtAfterFinal =
    !!overview.payment.debt_after_final_registration && overview.bed_status === 'completed';

  return (
    <Card className="p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ClipboardList className="size-5 text-zinc-400" />
          <h2 className="text-xl font-semibold text-zinc-900">Bulletin d'engagement</h2>
        </div>
        <Badge className={`${colors.bg} ${colors.text} ${colors.border} border`}>
          {CREW_STATUS_LABELS[status] ?? overview.bed_status_label}
        </Badge>
      </div>

      {/* Cahier L41 : quand tout est réglé, le dossier est PRÊT — le passage en
          Inscrit définitif reste une action administrative explicite. */}
      {/* TICKET-DEROG-04 : le passage en Inscrit définitif n'est proposé
          qu'après un contrôle explicite « aucune dérogation ne manque » pour
          CET équipage (ni pendant le chargement, ni si le contrôle échoue) ;
          le staff peut toujours forcer le statut plus bas, en connaissance de cause. */}
      {overview.ready_for_final_registration && derogationCheck?.block === null && (
        <p className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
          Contrôle de la dérogation d’âge indisponible : rechargez la fiche avant l’inscription
          définitive.
        </p>
      )}
      {overview.ready_for_final_registration && derogationCheck?.block?.blocked === false && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5">
          <p className="text-sm text-emerald-800">
            <strong>Prêt pour l'inscription définitive.</strong> Solde réglé, aucun élément
            manquant.
          </p>
          <Button
            size="sm"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              const previous = overview.bed_status;
              const result = await setCrewBedStatus(crewId, 'completed');
              applyResult(result, previous);
              setBusy(false);
            }}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Passer en Inscrit définitif
          </Button>
        </div>
      )}

      {/* TICKET-DEROG-04 : pourquoi le paiement du solde est bloqué. */}
      {derogation && (
        <BedDerogationBlockBanner
          block={derogation}
          context={derogationContext}
          showMissing={showDerogationMissing}
        />
      )}

      {/* Éléments manquants (cahier L15-16, L71-76) */}
      <div className="mb-5">
        <div className="mb-2 flex items-center gap-2">
          <h3 className="text-sm font-semibold text-zinc-900">Éléments manquants</h3>
          {outstanding.length > 0 ? (
            <Badge className="border border-orange-200 bg-orange-50 text-orange-700">
              {outstanding.length}
            </Badge>
          ) : (
            <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-700">
              Aucun
            </Badge>
          )}
          {overview.info_deadline && (
            <span className="text-xs text-zinc-400">
              à fournir avant le {formatDate(overview.info_deadline)}
            </span>
          )}
        </div>

        {overview.missing.source === 'none' ? (
          <p className="text-sm text-zinc-400">
            Aucun bulletin renseigné pour cet équipage à ce jour.
          </p>
        ) : outstanding.length === 0 && resolved.length === 0 ? (
          <p className="text-sm text-emerald-700">
            Bulletin complet — aucune information manquante.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {outstanding.map((item) => (
              <MissingItemRow
                key={item.code}
                item={item}
                busy={busy}
                onResolve={handleResolve}
                onUnresolve={handleUnresolve}
                onRemove={handleRemoveCustom}
              />
            ))}
            {resolved.map((item) => (
              <MissingItemRow
                key={item.code}
                item={item}
                busy={busy}
                onResolve={handleResolve}
                onUnresolve={handleUnresolve}
                onRemove={handleRemoveCustom}
              />
            ))}
          </ul>
        )}

        {/* 00253 (retour 29) : élément manquant personnalisé + relance */}
        {overview.missing.source !== 'none' && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={customLabel}
              onChange={(event) => setCustomLabel(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void handleAddCustom();
              }}
              placeholder="Ajouter un élément manquant (ex. certificat non conforme)"
              disabled={busy}
              className="h-9 min-w-[16rem] flex-1 rounded-md border border-zinc-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
            />
            <Button
              size="sm"
              variant="outline"
              disabled={busy || !customLabel.trim()}
              onClick={handleAddCustom}
            >
              <Plus className="mr-1.5 size-3.5" />
              Ajouter
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy || outstanding.length === 0}
              onClick={handleSendReminder}
              title="E-mail avec la liste des éléments manquants et le lien du bulletin pré-rempli"
            >
              <Mail className="mr-1.5 size-3.5" />
              Envoyer une relance
            </Button>
          </div>
        )}
      </div>

      {/* Pièces : signatures + certificat médical (jamais bloquant, cahier L120) */}
      <div className="mb-5 grid grid-cols-2 gap-4">
        <div>
          <h3 className="mb-1.5 text-sm font-semibold text-zinc-900">Signatures</h3>
          <ul className="flex flex-col gap-1 text-sm">
            {(['pilot', 'copilot'] as const).map((role) => {
              const signature = signatures[role];
              return (
                <li key={role} className="flex items-center gap-2">
                  {signature ? (
                    <Check className="size-4 text-emerald-600" />
                  ) : (
                    <AlertTriangle className="size-4 text-orange-500" />
                  )}
                  <span className="text-zinc-700">
                    {role === 'pilot' ? 'Pilote' : 'Copilote'}
                    {signature && (
                      <span className="text-zinc-400"> — {formatDate(signature.signed_at)}</span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <div>
          <h3 className="mb-1.5 text-sm font-semibold text-zinc-900">Certificat médical</h3>
          <ul className="flex flex-col gap-1 text-sm">
            {(['pilot', 'copilot'] as const).map((role) => {
              const certificate = certificates[role];
              return (
                <li key={role} className="flex items-center gap-2">
                  {certificate ? (
                    <Check className="size-4 text-emerald-600" />
                  ) : (
                    <span className="size-4 text-center text-xs text-zinc-300">—</span>
                  )}
                  <span className="text-zinc-700">
                    {role === 'pilot' ? 'Pilote' : 'Copilote'}
                    {certificate ? (
                      <span className="text-zinc-400"> — {certificate.name}</span>
                    ) : (
                      <span className="text-zinc-400"> — non déposé</span>
                    )}
                  </span>
                  {/* 00253 (retour 28) : en attente → voir → valider / refuser */}
                  {certificate && (
                    <span className="ml-auto flex items-center gap-1">
                      <Badge
                        className={
                          certificate.review?.status === 'validated'
                            ? 'bg-emerald-100 text-emerald-800'
                            : certificate.review?.status === 'rejected'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-amber-100 text-amber-800'
                        }
                      >
                        {certificate.review?.status === 'validated'
                          ? 'Validé'
                          : certificate.review?.status === 'rejected'
                            ? 'Refusé'
                            : 'En attente de validation'}
                      </Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => handleViewCertificate(role, certificate.path)}
                        title="Voir le certificat"
                      >
                        <ExternalLink className="size-3.5" />
                      </Button>
                      {certificate.review?.status !== 'validated' && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy || viewedCertificates[role] !== certificate.path}
                          title={
                            viewedCertificates[role] === certificate.path
                              ? undefined
                              : 'Consultez le certificat avant de décider'
                          }
                          onClick={() => handleReviewCertificate(role, 'validated')}
                        >
                          Valider
                        </Button>
                      )}
                      {certificate.review?.status !== 'rejected' && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy || viewedCertificates[role] !== certificate.path}
                          title={
                            viewedCertificates[role] === certificate.path
                              ? undefined
                              : 'Consultez le certificat avant de décider'
                          }
                          onClick={() => handleReviewCertificate(role, 'rejected')}
                          className="text-red-700"
                        >
                          Refuser
                        </Button>
                      )}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Solde BED — le CRM reste seul maître du montant dû */}
      <div className="mb-5">
        <h3 className="mb-1.5 text-sm font-semibold text-zinc-900">Solde</h3>
        {overview.payment.exists ? (
          <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-zinc-700">
              <span>
                Dû <strong>{formatCents(overview.payment.amount_cents)}</strong>
              </span>
              <span>
                Réglé <strong>{formatCents(overview.payment.paid_cents)}</strong>
              </span>
              <span>
                Reste <strong>{formatCents(overview.payment.remaining_cents)}</strong>
              </span>
            </div>
            <p className="mt-1 text-xs text-zinc-500">
              {overview.payment.settled
                ? 'Solde intégralement réglé.'
                : nothingDue
                  ? 'Rien à payer : voir l’encadré ci-dessous.'
                  : 'Solde non soldé — le participant peut reprendre son paiement.'}
            </p>
            {/* Recette du 07/10 : le staff cherchait comment ajouter l'assurance
                annulation après coup. Miroir du verrou de set_bed_case_options. */}
            <p className="mt-1 text-xs text-zinc-500">
              {overview.payment.method ||
              (overview.payment.paid_cents ?? 0) > 0 ||
              !['draft', 'pending_card'].includes(overview.payment.status ?? '')
                ? 'Options figées : un moyen de paiement a été choisi ou un versement reçu. Aucun lien ne permet encore de payer une option ajoutée après coup (ex. assurance annulation).'
                : 'Options (ex. assurance annulation) : le participant peut encore les modifier depuis son lien, tant qu’il n’a pas choisi de moyen de paiement.'}
            </p>
          </div>
        ) : (
          <p className="text-sm text-zinc-400">
            {overview.payment.nothing_due
              ? ['pre_registered', 'bed_downloaded'].includes(overview.bed_status)
                ? 'Rien à payer : le solde est couvert par l’acompte et la réduction. Si l’acompte est porté par l’équipage, la partie paiement sera validée à l’envoi du bulletin ; sinon (acompte validé sur un membre) ou si le statut ne change pas après l’envoi, utilisez « Forcer le statut BED ».'
                : 'Rien à payer : le solde est couvert par l’acompte et la réduction.'
              : debtReopened || debtAfterFinal
                ? 'Aucun dossier de solde BED pour cet équipage, mais un solde est de nouveau dû (voir ci-dessous).'
                : 'Aucun dossier de solde BED pour cet équipage.'}
            {overview.payment.nothing_due &&
              solde &&
              solde.live_total_cents < 0 &&
              ` Surplus : ${formatCents(-solde.live_total_cents)} (acompte et réduction au-delà du total), à voir avec la comptabilité.`}
          </p>
        )}
        {/* 00305 (TICKET-BED-32) : bulletin envoyé sans paiement, puis la
            réduction ou la grille a changé. Le serveur a passé l'équipage en
            « BED incomplet — paiement » ; il faut un lien de paiement. */}
        {debtReopened && (
          <div
            role="alert"
            className="mt-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-red-600" />
            <div>
              <p className="font-semibold">
                Solde dû : {formatCents(overview.payment.remaining_cents ?? 0)}
              </p>
              <p className="mt-0.5">
                Le bulletin a été envoyé sans paiement (rien à payer), puis la réduction ou la
                grille a changé. Générez un lien de paiement dans la section « Paiements » de la
                fiche (« Générer un lien BED ») et envoyez-le à l&apos;équipage : le statut passera
                à « BED complet » une fois le solde réglé.
              </p>
            </div>
          </div>
        )}
        {/* 00306 (TICKET-BED-32 Y2) : même cas sur un équipage déjà Inscrit
            définitif. Le serveur ne touche pas au statut : la décision reste
            au staff. */}
        {debtAfterFinal && (
          <div
            role="alert"
            className="mt-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-red-600" />
            <div>
              <p className="font-semibold">
                Solde dû après l’inscription définitive :{' '}
                {formatCents(overview.payment.remaining_cents ?? 0)}
              </p>
              <p className="mt-0.5">
                Le bulletin a été envoyé sans paiement (rien à payer), puis la réduction ou la
                grille a changé alors que l&apos;équipage est déjà inscrit définitivement. Le statut
                « Inscrit définitif » ne change pas tout seul et aucune relance ne part. Vérifiez la
                réduction : si ce solde est bien dû, générez un lien de paiement dans la section «
                Paiements » de la fiche (« Générer un lien BED ») et envoyez-le à l&apos;équipage.
              </p>
            </div>
          </div>
        )}
        {/* 00305 (TICKET-BED-32) : total couvert en apparence, mais par un
            acompte attesté sur un membre ou un contact, qui peut déjà couvrir
            un autre équipage : ne pas inviter à forcer le statut. */}
        {overview.payment.acompte_not_on_crew && (
          <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
            <div>
              <p className="font-semibold">Acompte validé sur un membre, pas sur l&apos;équipage</p>
              <p className="mt-0.5">
                Le total paraît couvert, mais l&apos;acompte retenu est attesté sur un membre ou un
                contact : il peut déjà couvrir un autre équipage de ce participant. Ne forcez pas le
                statut : vérifiez à quel équipage cet acompte se rattache, puis validez
                l&apos;acompte directement sur l&apos;équipage concerné.
              </p>
            </div>
          </div>
        )}
        {/* 00305 : total couvert par la réduction, mais aucun acompte enregistré :
            pas de lien du bulletin possible, ne pas forcer le statut. */}
        {overview.payment.acompte_missing && (
          <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
            <div>
              <p className="font-semibold">Aucun acompte enregistré pour cet équipage</p>
              <p className="mt-0.5">
                Le total paraît couvert par la réduction, mais aucun acompte n&apos;est enregistré
                (ni BPI payé, ni acompte validé) : le lien du bulletin ne peut pas être émis. Ne
                forcez pas le statut : si le règlement a eu lieu hors module (ancienne plateforme,
                exonération, report), validez l&apos;acompte sur l&apos;équipage dans la section «
                Paiements » (0 € si rien n&apos;a été versé).
              </p>
            </div>
          </div>
        )}
        {/* 00305 : BPI payé par l'équipage, mais un lien de bulletin émis au nom
            du payeur est encore actif (il pourrait déduire l'acompte deux fois). */}
        {overview.payment.contact_link_active && (
          <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
            <div>
              <p className="font-semibold">Lien de bulletin du payeur encore actif</p>
              <p className="mt-0.5">
                Le BPI est payé par l&apos;équipage, mais un lien de bulletin émis au nom du payeur
                (avant son rattachement à l&apos;équipage) est encore actif : il pourrait déduire
                l&apos;acompte une seconde fois. Ne forcez pas le statut : faites révoquer ce lien
                par l&apos;équipe technique (aucun écran ne le permet), le bulletin de
                l&apos;équipage pourra ensuite être validé sans paiement.
              </p>
            </div>
          </div>
        )}
        {/* Recette du 07/10 : une remise « Engagement offert » coupait le
            paiement sans que la fiche dise pourquoi ni quoi faire. */}
        {nothingDue && solde && (
          <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
            <div>
              <p className="font-semibold">Rien à payer pour cet équipage</p>
              <p className="mt-0.5">
                Total calculé : {formatCents(solde.prix_total_cents)} − acompte{' '}
                {formatCents(solde.acompte_cents)} − remise {formatCents(solde.reduction_cents)}
                {solde.options_cents > 0 && ` + options ${formatCents(solde.options_cents)}`} ={' '}
                {formatCents(solde.live_total_cents)} : il n&apos;y a rien à faire payer
                {overview.payment.exists
                  ? ' (le dossier de solde ci-dessus affiche encore son ancien montant : n’envoyez pas de lien de paiement).'
                  : '.'}{' '}
                Vérifiez le bulletin (onglet « Bulletin d&apos;engagement »), puis passez
                l&apos;équipage au bon statut avec « Forcer le statut BED » ci-dessous.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Lien du formulaire + override de statut (cahier L15) */}
      <div className="flex flex-col gap-3 border-t border-zinc-200 pt-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* (00242) Un lien ACTIF se copie ; le mint n'est proposé QUE s'il
              n'y en a pas (ou plus). L'émission est idempotente côté serveur :
              « régénérer » rendait le même jeton, le bouton mentait. */}
          {hasActiveLink ? (
            <Button
              size="sm"
              variant="outline"
              onClick={handleCopyActiveLink}
              className="border-teal-300 text-teal-700 hover:bg-teal-50"
            >
              <Copy className="mr-2 size-4" />
              Copier le lien actif
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setLinkDialogOpen(true)}
              className="border-teal-300 text-teal-700 hover:bg-teal-50"
            >
              <FileText className="mr-2 size-4" />
              {overview.link ? 'Régénérer le lien BED' : 'Générer le lien BED'}
            </Button>
          )}
          {/* (00293, CX-6) Révoquer ferme aussi les liens de paiement du solde,
              qui peuvent survivre au lien du bulletin : proposé dès qu'un lien a
              existé ou qu'un dossier de solde est ouvert. */}
          {(overview.link || overview.payment.exists) && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setRevokeDialogOpen(true)}
              className="border-red-200 text-red-700 hover:bg-red-50"
            >
              <Ban className="mr-2 size-4" />
              Révoquer les liens
            </Button>
          )}
          {overview.link && (
            <span className="inline-flex items-center gap-1.5 text-xs text-zinc-500">
              <LinkIcon className="size-3.5" />
              {overview.link.expired
                ? `lien expiré le ${formatDate(overview.link.expires_at)}`
                : `lien actif jusqu'au ${formatDate(overview.link.expires_at)}`}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-zinc-500" htmlFor="bed-status-override">
              Forcer le statut BED
            </label>
            <Select
              id="bed-status-override"
              className="w-64"
              value={pendingStatus}
              placeholder="Choisir un statut…"
              disabled={busy}
              options={OVERRIDABLE_STATUSES.map((value) => ({
                value,
                label: CREW_STATUS_LABELS[value],
              }))}
              onChange={(e) => setPendingStatus(e.target.value)}
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={busy || !pendingStatus || pendingStatus === overview.bed_status}
            onClick={handleApplyStatus}
          >
            Appliquer
          </Button>
        </div>
      </div>

      <BedRevokeLinksDialog
        open={revokeDialogOpen}
        onOpenChange={setRevokeDialogOpen}
        crewId={crewId}
        crewName={crewName}
        onRevoked={() => {
          void load();
          onStatusChanged?.();
        }}
      />

      <BedFormLinkDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        crewId={crewId}
        crewName={crewName}
        onGenerated={() => {
          void load();
          onStatusChanged?.();
        }}
      />
    </Card>
  );
}
